/**
 * Component displaying the results of the Home Page module
 * for AI Music Detector.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025
 */

import { useState } from 'react'
import { IampLogger, LogTag } from '../../../utils/IampLogger'
import TimeAnalysisDialog, { TimeAnalysisSeries } from '../../TimeAnalysisChart'
import '../styles/homeresult.css'
import AiMusicDetectorBadge from './AiMusicDetectorBadge'
import { AiMusicDetectorResult } from './AiMusicDetectorResult'
import './styles/ai-music-detector-result.css'

const enum AiMusicDetectorText {
  Title = 'AI Probability',
  ModelLabel = 'Detected model',
  Disclaimer = 'The AI detector can make mistakes. ' +
    'It is recommended to verify the results. ' +
    "The detector's output should be considered as an indicator.",
}

const HomeAiMusicDetectorResult = (props: AiMusicDetectorResult) => {
  IampLogger.debug(LogTag.HomePage, 'Result props:', props)

  const [isDialogVisible, setDialogVisible] = useState(false)
  const displayedConfidence =
    props.confidence === undefined
      ? undefined
      : props.isAi
        ? props.confidence
        : 100 - props.confidence
  const timeAnalysisSeries = props.timeAnalysis
    ? [
        new TimeAnalysisSeries(
          props.timeAnalysis.timestamps,
          props.timeAnalysis.aiProbability,
          props.fileName ?? '',
          props.isAi ?? false
        ),
      ]
    : null

  const modelToDisplay = props.suspectedModel && props.modelVersion
    ? `${props.suspectedModel} v${props.modelVersion}`
    : props.suspectedModel

  return (
    <>
      <div className="module-result ai-music-detector-result">
        <div className="result-badge">
          <AiMusicDetectorBadge isAi={props.isAi ?? false} />
        </div>
        <div className="module-data">
          <div className="data-title">{AiMusicDetectorText.Title}: {displayedConfidence}%*</div>

          <div className="ai-music-detector-detail">
            <div className="data-detail ai-music-detector-result">
              {props.suspectedModel && (
                <>
                  <div>{AiMusicDetectorText.ModelLabel}:</div>
                  <div className="data-detail-value" id="ai-model">
                    {modelToDisplay}
                  </div>
                </>
              )}
            </div>
          </div>

          {props.timeAnalysis && (
            <div className="data-detail2">
              <button
                id="button-time-analysis"
                type="button"
                className="linkBtn"
                onClick={() => {
                  if (props.timeAnalysis) {
                    setDialogVisible(true)
                  }
                }}
              >
                Time analysis
              </button>
            </div>
          )}

          <div className="ai-music-detect-disclaimer">
            *{AiMusicDetectorText.Disclaimer}
          </div>
        </div>
      </div>

      {props.timeAnalysis && timeAnalysisSeries && (
        <TimeAnalysisDialog
          id="time-analysis-dialog"
          series={timeAnalysisSeries}
          changeVisible={setDialogVisible}
          isVisible={isDialogVisible}
        />
      )}
    </>
  )
}

export default HomeAiMusicDetectorResult
